/**
 * Sprint V102.j (So 27.09.2026) — CampaignModal
 * ---------------------------------------------------------------------------
 * In-App-Message-Modal, das die naechste noch nicht dismissed'e Kampagne
 * fuer den User rendert (Frequency-Cap = 1 pro User pro Kampagne).
 * Wird im DashboardLayout gemountet und triggert automatisch bei jedem
 * Route-Enter, wenn eine passende Kampagne existiert.
 */
import React, { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function CampaignModal() {
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user === false || !user.email) return;
    let cancelled = false;
    api
      .get("/campaigns/next")
      .then((r) => {
        if (cancelled) return;
        const c = r.data?.campaign || null;
        if (c) {
          setCampaign(c);
          setOpen(true);
        }
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const dismiss = async () => {
    if (!campaign?.key) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await api.post("/campaigns/dismiss", { key: campaign.key });
    } catch {
      /* silent */
    }
    setBusy(false);
    setOpen(false);
  };

  const handlePrimary = async () => {
    // Erst dismissen, dann navigieren — damit der User die Kampagne
    // nicht bei jedem Route-Wechsel neu bekommt.
    if (campaign?.key) {
      try {
        await api.post("/campaigns/dismiss", { key: campaign.key });
      } catch {
        /* silent */
      }
    }
    if (campaign?.cta_primary_url) {
      window.location.href = campaign.cta_primary_url;
    } else {
      setOpen(false);
    }
  };

  if (!open || !campaign) return null;

  return (
    <div
      data-testid={`campaign-modal-${campaign.key}`}
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`campaign-modal-${campaign.key}-headline`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={dismiss}
        aria-hidden="true"
      />
      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7">
        <button
          type="button"
          data-testid={`campaign-modal-${campaign.key}-close`}
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-slate-100 text-slate-500 transition-colors"
          aria-label="Schließen"
        >
          <X className="w-5 h-5" />
        </button>
        <h2
          id={`campaign-modal-${campaign.key}-headline`}
          data-testid={`campaign-modal-${campaign.key}-headline`}
          className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-tight pr-6"
        >
          {campaign.headline}
        </h2>
        <p
          data-testid={`campaign-modal-${campaign.key}-body`}
          className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed whitespace-pre-line"
        >
          {campaign.body}
        </p>
        <div className="mt-6 flex flex-col sm:flex-row-reverse gap-2 sm:gap-3">
          <button
            type="button"
            data-testid={`campaign-modal-${campaign.key}-cta-primary`}
            onClick={handlePrimary}
            disabled={busy}
            className="inline-flex items-center justify-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
          >
            {campaign.cta_primary_label}
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-testid={`campaign-modal-${campaign.key}-cta-secondary`}
            onClick={dismiss}
            disabled={busy}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-60 transition-colors"
          >
            {campaign.cta_secondary_label || "Vielleicht später"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CampaignModal;
